import React, { useEffect, useState, useRef } from 'react';
import inkUtopia from '../images/ink_utopia.png';
import logoMark from '../images/ic_text.png';
import selena from '../images/characters/selena.png';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const Dashboard = () => {
  const [me, setMe] = useState({ connected: false });
  const [creator, setCreator] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [caption, setCaption] = useState('');
  // Privacy must be manually selected per TikTok guidelines; start as empty
  const [privacy, setPrivacy] = useState('');
  // UX requires manual toggles default OFF (we store allow* and invert for disable flags)
  const [allowComments, setAllowComments] = useState(false);
  const [allowDuet, setAllowDuet] = useState(false);
  const [allowStitch, setAllowStitch] = useState(false);
  // Commercial content disclosure basics
  const [isCommercial, setIsCommercial] = useState(false);
  const [yourBrand, setYourBrand] = useState(false);
  const [brandedContent, setBrandedContent] = useState(false);
  const [agreePolicy, setAgreePolicy] = useState(false);
  const [maxDurationSec, setMaxDurationSec] = useState(null);
  const [privacyOptions, setPrivacyOptions] = useState([]);
  const [jobStatus, setJobStatus] = useState('idle'); // idle|pending|processing|success|failed
  const dropRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/me`)
      .then(r => r.json())
      .then(setMe)
      .catch(() => setMe({ connected: false }));
    // Fetch creator_info to populate privacy/toggle constraints
    fetch(`${API_BASE}/api/tiktok/creator_info`).then(r => r.json()).then((data) => {
      if (data && data.creator && data.creator.data) {
        const c = data.creator.data;
        setCreator(c);
        const p = c.privacy_level_options || [];
        setPrivacyOptions(Array.isArray(p) ? p : []);
        const maxDur = c.max_video_post_duration_sec;
        if (typeof maxDur === 'number') setMaxDurationSec(maxDur);
      }
    }).catch(() => {});
  }, []);
  const handleConnect = () => {
    window.location.href = `${API_BASE}/oauth/tiktok`;
  };

  const handlePost = async () => {
    if (!privacy) { alert('Please select a Privacy option.'); return; }
    if (isCommercial && brandedContent && privacy === 'SELF_ONLY') {
      alert('Branded content cannot be private. Please choose Public or Friends.');
      return;
    }
    if (!agreePolicy) { alert('Please confirm the policy consent before posting.'); return; }
    setJobStatus('pending');
    const resp = await fetch(`${API_BASE}/api/tiktok/post`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        media_url: mediaUrl,
        caption,
        privacy,
        disable_comments: !allowComments,
        disable_duet: !allowDuet,
        disable_stitch: !allowStitch,
        is_commercial: !!isCommercial,
        commercial_your_brand: !!yourBrand,
        commercial_third_party: !!brandedContent,
      }),
    });
    const data = await resp.json();
    const id = data.jobId || '';
    if (!id) { setJobStatus('failed'); return; }
    // start polling
    const start = Date.now();
    const poll = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/tiktok/post/status?jobId=${encodeURIComponent(id)}`);
        const j = await r.json();
        if (j.status === 'success' || j.status === 'failed') {
          setJobStatus(j.status);
          return;
        }
        setJobStatus(j.status || 'processing');
      } catch (e) {
        // ignore and retry a few times
      }
      if (Date.now() - start < 120000) setTimeout(poll, 2000);
      else setJobStatus('failed');
    };
    poll();
  };

  const pageStyle = {
    position: 'relative',
    minHeight: '100vh',
    fontFamily: 'Lato, sans-serif',
    color: '#e8f6f8',
    backgroundColor: '#031b22',
    overflowY: 'auto'
  };
  const bgStyle = {
    position: 'absolute',
    inset: 0,
    backgroundImage: `url(${inkUtopia})`,
    backgroundSize: 'cover',
    backgroundPosition: 'top center',
    opacity: 0.22,
    pointerEvents: 'none'
  };
  const logoStyle = {
    position: 'absolute', top: '20px', left: '20px', width: '260px', opacity: 0.22, pointerEvents: 'none'
  };
  const selenaStyle = {
    position: 'absolute', bottom: '0', left: '0', height: '82vh', opacity: 0.18, pointerEvents: 'none'
  };
  const containerStyle = { position: 'relative', zIndex: 1, maxWidth: 960, margin: '0 auto', padding: '2rem' };
  const headerBlock = { marginBottom: '0.5rem' };
  const card = { background: 'rgba(5,33,41,0.7)', border: '1px solid rgba(135,210,219,0.25)', borderRadius: 16, padding: '1.25rem', boxShadow: '0 0 18px rgba(135,210,219,0.08)' };
  const runeBtn = { background: '#C7F5F9', color: '#07384E', fontWeight: 900, letterSpacing: 0.3, border: '1px solid rgba(7,56,78,0.35)', borderRadius: 14, padding: '0.9rem 1.25rem', cursor: 'pointer', boxShadow: '0 0 14px rgba(199,245,249,0.25)' };
  const dropZone = { border: '2px dashed rgba(135,210,219,0.4)', borderRadius: 14, padding: '2rem', textAlign: 'center', background: 'rgba(3,27,34,0.4)' };
  const fieldLabel = { fontSize: 12, opacity: 0.9, marginTop: '0.75rem' };
  const selectStyle = { display: 'block', marginTop: 6, background: '#04232b', color: '#e8f6f8', border: '1px solid rgba(135,210,219,0.25)', borderRadius: 8, padding: '0.4rem' };
  const inputStyle = { width: '100%', background: '#04232b', color: '#e8f6f8', border: '1px solid rgba(135,210,219,0.25)', borderRadius: 8, padding: '0.6rem', marginTop: 6 };
  const postBtn = { ...runeBtn, marginTop: '0.75rem' };

  const validateAndUpload = async (file) => {
    const allowed = ['video/mp4','video/quicktime'];
    if (!allowed.includes(file.type)) { alert('Only MP4 or MOV allowed.'); return; }
    if (file.size > 200 * 1024 * 1024) { alert('File too large. Max 200MB.'); return; }
    // Duration/aspect checks
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = async () => {
      const dur = v.duration || 0;
      const limit = maxDurationSec || 60;
      if (dur > limit) { alert(`Video too long. Keep it ≤ ${limit}s.`); }
      const vertical = v.videoHeight >= v.videoWidth;
      if (!vertical) { alert('Tip: TikTok prefers vertical 9:16.'); }
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API_BASE}/api/uploads`, { method: 'POST', body: fd });
      const j = await r.json(); setMediaUrl(j.mediaUrl || '');
      URL.revokeObjectURL(v.src);
    };
    v.src = URL.createObjectURL(file);
  };

  return (
    <div style={pageStyle}>
      <div style={bgStyle} />
      <img src={logoMark} alt="Isadoran Creed mark" style={logoStyle} />
      <img src={selena} alt="Selena overlay" style={selenaStyle} />
      <div style={containerStyle}>
        <div style={headerBlock} />

        {!me.connected && (
          <div style={{ ...card, maxWidth: 560 }}>
            <div style={{ fontSize: '1.1rem', marginBottom: 12 }}>Step into the sanctum, connect your TikTok identity.</div>
            <button onClick={handleConnect} style={runeBtn}>Connect TikTok</button>
          </div>
        )}

        {me.connected && (
          <div>
           
            <div style={{ ...card }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: 4 }}>Drop your Reel into the Fire</div>
              <div ref={dropRef}
                   onDragOver={(e)=>{e.preventDefault();}}
                   onDrop={async (e)=>{ e.preventDefault(); const f=e.dataTransfer.files?.[0]; if(!f) return; const fd=new FormData(); fd.append('file',f); const r=await fetch(`${API_BASE}/api/uploads`,{method:'POST',body:fd}); const j=await r.json(); setMediaUrl(j.mediaUrl||''); }}
                   style={dropZone}>
                <div>Drag & drop an MP4/MOV here, or choose a file</div>
                <form onSubmit={async (e)=>{ e.preventDefault(); const input=e.currentTarget.querySelector('input[type=file]'); if(!input.files||!input.files[0]) return; await validateAndUpload(input.files[0]); }}>
                  <input type="file" accept="video/mp4,video/quicktime" style={{ marginTop: 10 }} />
                  <button type="submit" style={{ ...runeBtn, marginLeft: 8 }}>Upload</button>
                </form>
              </div>

              {mediaUrl && (
                <div style={{ marginTop: '1rem' }}>
                  <video src={mediaUrl} style={{ width: '100%', borderRadius: 12, background:'#000' }} controls muted />
                  <div style={fieldLabel}>Caption</div>
                  <textarea value={caption} onChange={e=>setCaption(e.target.value)} rows={3} maxLength={150} style={inputStyle} placeholder="Trail of Light #IsadoranCreed" />

                  <div style={fieldLabel}>Privacy (choose one)</div>
                  <select value={privacy} onChange={e=>setPrivacy(e.target.value)} style={selectStyle}>
                    <option value="" disabled>-- Select privacy --</option>
                    {(privacyOptions.length ? privacyOptions : ['SELF_ONLY','FRIENDS','PUBLIC']).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>

                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8, marginTop: 14 }}>
                      <input type="checkbox" style={{ accentColor: '#C7F5F9' }} checked={allowComments} onChange={e=>setAllowComments(e.target.checked)} /> Allow comments
                    </label>
                    <label style={{ display:'flex', alignItems:'center', gap:8, marginTop: 14 }}>
                      <input type="checkbox" style={{ accentColor: '#C7F5F9' }} checked={allowDuet} onChange={e=>setAllowDuet(e.target.checked)} /> Allow duet
                    </label>
                    <label style={{ display:'flex', alignItems:'center', gap:8, marginTop: 14 }}>
                      <input type="checkbox" style={{ accentColor: '#C7F5F9' }} checked={allowStitch} onChange={e=>setAllowStitch(e.target.checked)} /> Allow stitch
                    </label>
                  </div>

                  <div style={{ ...fieldLabel, marginTop: '0.75rem' }}>Commercial content</div>
                  <label style={{ display:'flex', alignItems:'center', gap:8, marginTop: 8 }}>
                    <input type="checkbox" style={{ accentColor: '#C7F5F9' }} checked={isCommercial} onChange={e=>{ setIsCommercial(e.target.checked); if(!e.target.checked){ setYourBrand(false); setBrandedContent(false);} }} /> This content promotes a brand/product/service
                  </label>
                  {isCommercial && (
                    <div style={{ display:'flex', gap:'1rem', marginTop: 8, flexWrap:'wrap' }}>
                      <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <input type="checkbox" style={{ accentColor: '#C7F5F9' }} checked={yourBrand} onChange={e=>setYourBrand(e.target.checked)} /> Your brand
                      </label>
                      <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <input type="checkbox" style={{ accentColor: '#C7F5F9' }} checked={brandedContent} onChange={e=>setBrandedContent(e.target.checked)} /> Branded content
                      </label>
                      {(brandedContent && privacy === 'SELF_ONLY') && (
                        <div style={{ color:'#ffaaaa' }}>Branded content cannot be private. Choose Public or Friends.</div>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: 10, fontSize: 12, opacity: 0.9 }}>
                    By posting, you agree to TikTok's {isCommercial ? (brandedContent ? 'Branded Content Policy and Music Usage Confirmation' : 'Music Usage Confirmation') : 'Music Usage Confirmation'}.
                  </div>
                  <label style={{ display:'flex', alignItems:'center', gap:8, marginTop: 8 }}>
                    <input type="checkbox" style={{ accentColor: '#C7F5F9' }} checked={agreePolicy} onChange={e=>setAgreePolicy(e.target.checked)} /> I understand and agree
                  </label>

                  <button
                    disabled={!mediaUrl || !caption || !privacy || (isCommercial && brandedContent && privacy==='SELF_ONLY') || !agreePolicy}
                    onClick={handlePost}
                    style={postBtn}
                  >Post to TikTok</button>

                  {jobStatus !== 'idle' && (
                    <div style={{ marginTop: '0.75rem' }}>
                      {jobStatus === 'pending' && 'Awaiting TikTok’s blessing…'}
                      {jobStatus === 'processing' && 'Casting to TikTok…'}
                      {jobStatus === 'success' && 'Reel successfully carried to TikTok.'}
                      {jobStatus === 'failed' && 'The Veil rejected this relic. Try again.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;


